/**
 * Axios Retry Interceptor
 * Automatically retries failed requests with exponential backoff
 */

/**
 * Determine if a request should be retried based on error
 * @param {Error} error - The error object from axios
 * @returns {boolean} - Whether to retry the request
 */
export const shouldRetry = (error) => {
  // No response means network error - should retry
  if (!error.response) {
    return true;
  }

  const status = error.response.status;

  // Retry on rate limit (429)
  if (status === 429) {
    return true;
  }

  // Retry on server errors (5xx)
  if (status >= 500) {
    return true;
  }

  // Don't retry client errors (4xx except 429)
  if (status >= 400 && status < 500) {
    return false;
  }

  // Retry on timeout
  if (error.code === 'ECONNABORTED') {
    return true;
  }

  return false;
};

/**
 * Determine if HTTP method is safe to retry
 * @param {string} method - HTTP method
 * @returns {boolean} - Whether method is safe to retry
 */
export const isSafeToRetry = (method) => {
  const safeMethod = method.toUpperCase();
  // GET, HEAD, OPTIONS, PUT (idempotent), DELETE (idempotent) are safe
  return ['GET', 'HEAD', 'OPTIONS', 'PUT', 'DELETE'].includes(safeMethod);
};

/**
 * Calculate retry delay with exponential backoff
 * @param {number} retryCount - Current retry attempt number
 * @param {number} baseDelay - Base delay in milliseconds
 * @returns {number} - Delay in milliseconds
 */
export const getRetryDelay = (retryCount, baseDelay = 1000) => {
  // Exponential backoff: 1s, 2s, 4s, 8s (max 10s)
  const delay = Math.min(baseDelay * Math.pow(2, retryCount), 10000);

  // Add jitter (random 0-20%) to prevent thundering herd
  const jitter = delay * 0.2 * Math.random();

  return delay + jitter;
};

/**
 * Check if request config explicitly disables retry
 * @param {Object} config - Axios request config
 * @returns {boolean} - Whether retry is disabled
 */
export const isRetryDisabled = (config) => {
  return config.retry === false || config.retryCount === 0;
};

/**
 * Setup retry interceptor for axios instance
 * @param {AxiosInstance} axiosInstance - The axios instance to add interceptor to
 * @param {Object} options - Retry configuration options
 */
export const setupRetryInterceptor = (axiosInstance, options = {}) => {
  const {
    maxRetries = 3,
    baseDelay = 1000,
    retryOnlyIdempotent = true,
  } = options;

  axiosInstance.interceptors.response.use(
    (response) => response,
    async (error) => {
      const config = error.config;

      // Guard: if no config (non-Axios error, CORS, or error from prior interceptor), skip retry
      if (!config) {
        return Promise.reject(error);
      }

      // Initialize retry count
      if (!config.__retryCount) {
        config.__retryCount = 0;
      }

      // Check if should retry
      const shouldRetryRequest =
        !isRetryDisabled(config) &&
        shouldRetry(error) &&
        config.__retryCount < maxRetries &&
        (!retryOnlyIdempotent || isSafeToRetry(config.method));

      if (!shouldRetryRequest) {
        return Promise.reject(error);
      }

      // Increment retry count
      config.__retryCount += 1;

      // Calculate and wait for delay
      const delay = getRetryDelay(config.__retryCount - 1, baseDelay);

      const isDevelopment = process.env.NODE_ENV === 'development';
      if (isDevelopment) {
        console.warn(
          `Retrying request (${config.__retryCount}/${maxRetries}):`,
          {
            url: config.url,
            method: config.method,
            delay: `${Math.round(delay)}ms`,
            reason: error.response?.status || error.message,
          }
        );
      }

      // Wait before retrying
      await new Promise((resolve) => setTimeout(resolve, delay));

      // Retry the request
      return axiosInstance(config);
    }
  );
};

/**
 * Retry configuration presets for common scenarios
 */
export const retryPresets = {
  // Aggressive retry for critical operations
  critical: {
    maxRetries: 5,
    baseDelay: 500,
    retryOnlyIdempotent: false,
  },

  // Standard retry for most operations
  standard: {
    maxRetries: 3,
    baseDelay: 1000,
    retryOnlyIdempotent: true,
  },

  // Conservative retry for non-idempotent operations
  conservative: {
    maxRetries: 2,
    baseDelay: 2000,
    retryOnlyIdempotent: true,
  },

  // No retry for operations that should never be retried
  disabled: {
    maxRetries: 0,
  },
};
